/**
 * <delivery-estimate>
 * Renders an expected delivery date (or date range) near the cart totals.
 * Computed client-side from the shopper's own clock so it's never a stale
 * cached date, with optional Saudi-weekend (Fri/Sat) skipping. Localized to
 * Arabic or English via Intl. Driven entirely by data-attributes set from the
 * cart block settings.
 */
if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        this.render();
      }

      render() {
        const textEl = this.querySelector('[data-delivery-text]');
        if (!textEl) return;

        const minDays = parseInt(this.dataset.minDays, 10) || 0;
        let maxDays = parseInt(this.dataset.maxDays, 10);
        if (isNaN(maxDays) || maxDays < minDays) maxDays = minDays;
        const skipWeekend = this.dataset.skipWeekend === 'true';
        const isArabic = this.dataset.locale === 'ar';
        const label = this.dataset.label || '';

        const start = this.addDays(new Date(), minDays, skipWeekend);
        const end = this.addDays(new Date(), maxDays, skipWeekend);

        // "ar-u-nu-latn" keeps Latin digits (13) with Arabic month/day names,
        // matching the desired "الأحد 13 يوليو" style. "en-GB" gives day-month order.
        const localeTag = isArabic ? 'ar-u-nu-latn' : 'en-GB';
        const dateText = this.formatDate(start, end, localeTag);

        textEl.textContent = label ? label + ': ' + dateText : dateText;
        this.classList.add('is-ready');
      }

      addDays(fromDate, days, skipWeekend) {
        const d = new Date(fromDate);
        d.setHours(0, 0, 0, 0);
        if (days <= 0) return d;
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const dow = d.getDay(); // 0=Sun … 5=Fri, 6=Sat
          if (skipWeekend && (dow === 5 || dow === 6)) continue;
          added++;
        }
        return d;
      }

      formatDate(start, end, localeTag) {
        if (start.getTime() === end.getTime()) {
          return new Intl.DateTimeFormat(localeTag, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }).format(start);
        }
        const sameMonth =
          start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
        const dayFmt = new Intl.DateTimeFormat(localeTag, { day: 'numeric' });
        const dayMonthFmt = new Intl.DateTimeFormat(localeTag, { day: 'numeric', month: 'long' });
        const from = sameMonth ? dayFmt.format(start) : dayMonthFmt.format(start);
        const range = from + ' – ' + dayMonthFmt.format(end);
        // Wrap the range in an LTR isolate (U+2066…U+2069) so "13 – 15 July"
        // keeps its logical order when embedded in an RTL (Arabic) sentence.
        return '\u2066' + range + '\u2069';
      }
    }
  );
}
